import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid,
  FormControlLabel, Switch, Alert, CircularProgress, Tooltip, Breadcrumbs, Link,
  InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Save, Search } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import axios from 'axios';

function FbAdvertiserCampaigns() {
  const navigate = useNavigate();
  const { advertiserId } = useParams();
  const [advertiser, setAdvertiser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetValue, setBudgetValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactivePaused, setShowInactivePaused] = useState(false);

  // Date filters
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  const [formData, setFormData] = useState({
    fb_page_id: '',
    campaign_name: '',
    ads_name: '',
    fb_campaign_id: '',
    fb_adset_id: '',
    fb_ad_id: '',
    fb_form_id: '',
    payout_per_lead: '',
    client_budget: '',
    status: 'active',
    pass_to_crm: true,
    n8n_webhook_url: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [advertiserId, period, customStart, customEnd]);

  const getDateRange = () => {
    const today = new Date();
    const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    if (period === 'custom' && customStart && customEnd) {
      return { start: formatDate(customStart), end: formatDate(customEnd) };
    }

    switch (period) {
      case 'today':
        return { start: formatDate(today), end: formatDate(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: formatDate(yesterday), end: formatDate(yesterday) };
      case 'last_week':
        // Calculate last week (Monday to Sunday of previous week)
        const currentDay = today.getDay(); // 0=Sunday, 1=Monday...
        const daysToLastSunday = currentDay === 0 ? 7 : currentDay;
        const lastSunday = new Date(today);
        lastSunday.setDate(today.getDate() - daysToLastSunday);
        const lastMonday = new Date(lastSunday);
        lastMonday.setDate(lastSunday.getDate() - 6);
        return { start: formatDate(lastMonday), end: formatDate(lastSunday) };
      case 'last_month':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: formatDate(lastMonthStart), end: formatDate(lastMonthEnd) };
      case 'month':
      default:
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start: formatDate(monthStart), end: formatDate(monthEnd) };
    }
  };

  const fetchData = async () => {
    try {
      const { start, end } = getDateRange();
      const [advRes, campaignsRes] = await Promise.all([
        axios.get(`/admin/facebook/advertisers/${advertiserId}/details`),
        axios.get(`/admin/facebook/campaigns/by-advertiser/${advertiserId}?start_date=${start}&end_date=${end}`)
      ]);
      setAdvertiser(advRes.data.data);
      setCampaigns(campaignsRes.data.data || []);
      console.log('CAMPAIGNS LOADED:', campaignsRes.data.data); 
 } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        fb_page_id: campaign.fb_page_id || '',
        campaign_name: campaign.campaign_name || '',
        ads_name: campaign.ads_name || '',
        fb_campaign_id: campaign.fb_campaign_id || '',
        fb_adset_id: campaign.fb_adset_id || '',
        fb_ad_id: campaign.fb_ad_id || '',
        fb_form_id: campaign.fb_form_id || '',
        payout_per_lead: campaign.payout_per_lead || '',
        client_budget: campaign.client_budget || '',
        status: campaign.status || 'active',
        pass_to_crm: campaign.pass_to_crm !== false,
        n8n_webhook_url: campaign.n8n_webhook_url || '',
        notes: campaign.notes || ''
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        fb_page_id: '',
        campaign_name: '',
        ads_name: '',
        fb_campaign_id: '',
        fb_adset_id: '',
        fb_ad_id: '',
        fb_form_id: '',
        payout_per_lead: '',
        client_budget: '',
        status: 'active',
        pass_to_crm: true,
        n8n_webhook_url: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.campaign_name) {
        setMessage({ type: 'error', text: 'Campaign name is required' });
        return;
      }

      const payload = { ...formData, advertiser_id: parseInt(advertiserId) };

      if (editingCampaign) {
        await axios.put(`/admin/facebook/campaigns/${editingCampaign.id}`, payload);
        setMessage({ type: 'success', text: 'Campaign updated' });
      } else {
        await axios.post('/admin/facebook/campaigns', payload);
        setMessage({ type: 'success', text: 'Campaign created' });
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save campaign' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`/admin/facebook/campaigns/${id}`);
      setMessage({ type: 'success', text: 'Campaign deleted' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete campaign' });
    }
  };

  const handleBudgetSave = async (campaignId) => {
    try {
      await axios.patch(`/admin/facebook/campaigns/${campaignId}/budget`, { client_budget: parseInt(budgetValue) || 0 });
      setEditingBudget(null);
      setBudgetValue('');
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update budget' });
    }
  };

  const startEditBudget = (campaign) => {
    setEditingBudget(campaign.id);
    setBudgetValue(campaign.client_budget || '');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const PeriodButton = ({ value, label }) => (
    <Box
      onClick={() => setPeriod(value)}
      sx={{
        px: 2,
        py: 1,
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: period === value ? 'primary.main' : 'grey.100',
        color: period === value ? 'white' : 'text.primary',
        fontWeight: period === value ? 600 : 400,
        fontSize: '0.875rem',
        '&:hover': {
          bgcolor: period === value ? 'primary.dark' : 'grey.200'
        }
      }}
    >
      {label}
    </Box>
  );

// Filter campaigns: active OR paused with activity (leads or spend) in period
  const filteredCampaigns = campaigns
    .filter(c => {
      // Always show active campaigns
      if (c.status === 'active') return true;
      // For paused/archived: show if has leads OR has spent money in period
      const hasLeads = (parseInt(c.total_leads) || 0) > 0;
      const hasSpend = parseFloat(c.total_cost) > 0;
      if (hasLeads || hasSpend) return true;
      // Show inactive paused only if toggle is on
      return showInactivePaused;
    })
    .filter(c => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        c.campaign_name?.toLowerCase().includes(search) ||
        c.ads_name?.toLowerCase().includes(search) ||
        c.fb_ad_id?.toLowerCase().includes(search)
);
    })

.sort((a, b) => {
      // Active first, then all others
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      return 0;
    });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      <Box>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/facebook/campaigns')}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Facebook Campaigns
          </Link>
          <Typography color="text.primary">{advertiser?.advertiser_name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/facebook/campaigns')}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" fontWeight={700}>
              {advertiser?.advertiser_name}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
            Add Campaign
          </Button>
        </Box>

        {/* Date Filters */}
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2}>
          <PeriodButton value="today" label="Today" />
          <PeriodButton value="yesterday" label="Yesterday" />
          <PeriodButton value="last_week" label="Last Week" />
          <PeriodButton value="month" label="This Month" />
          <PeriodButton value="last_month" label="Last Month" />
          <PeriodButton value="custom" label="Custom" />

          {period === 'custom' && (
            <Box display="flex" gap={2} ml={2}>
              <DatePicker
                label="From"
                value={customStart}
                onChange={(v) => setCustomStart(v)}
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
              <DatePicker
                label="To"
                value={customEnd}
                onChange={(v) => setCustomEnd(v)}
                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
              />
            </Box>
          )}
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search campaigns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />

       {/* Show inactive paused toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={showInactivePaused}
              onChange={(e) => setShowInactivePaused(e.target.checked)}
              size="small"
            />
          }
          label="Show inactive paused campaigns"
          sx={{ mb: 2 }}
        />

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Campaigns Table */}
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Campaign</strong></TableCell>
                <TableCell><strong>Ads Name</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Total Leads</strong></TableCell>
                <TableCell align="center"><strong>Positive</strong></TableCell>
                <TableCell align="center"><strong>Pending</strong></TableCell>
                <TableCell align="center"><strong>Trash</strong></TableCell>
                <TableCell align="center"><strong>CPA</strong></TableCell>
                <TableCell align="center"><strong>Client Budget</strong></TableCell>
                <TableCell align="center"><strong>Generated</strong></TableCell>
                <TableCell align="right"><strong>CPL</strong></TableCell>
                <TableCell align="right"><strong>CPL Positive</strong></TableCell>
                <TableCell align="right"><strong>Total Cost</strong></TableCell>
                <TableCell align="right"><strong>Payout/Lead</strong></TableCell>
                <TableCell align="right"><strong>Revenue</strong></TableCell>
                <TableCell align="right"><strong>Profit</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCampaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={17} align="center">
                    <Typography color="textSecondary" py={4}>
                      {searchTerm 
                        ? `No campaigns found matching "${searchTerm}"`
                        : 'No campaigns with activity in this period. Click "Add Campaign" to create one.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredCampaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.875rem">{campaign.campaign_name}</Typography>
                    {campaign.fb_ad_id && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Ad ID: {campaign.fb_ad_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.875rem">{campaign.ads_name || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={campaign.status} color={getStatusColor(campaign.status)} size="small" sx={{ color: 'white' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={600}>{campaign.total_leads || 0}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="success.main" fontWeight={600}>{campaign.positive_leads || 0}</Typography>
                    <Typography variant="caption" color="textSecondary">{campaign.percent_positive}%</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="warning.main">{campaign.pending_leads || 0}</Typography>
                    <Typography variant="caption" color="textSecondary">{campaign.percent_pending}%</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="error.main">{campaign.trash_leads || 0}</Typography>
                    <Typography variant="caption" color="textSecondary">{campaign.percent_trash}%</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ color: '#2196F3', fontWeight: 600 }}>{campaign.cpa_leads || 0}</Typography>
                    <Typography variant="caption" color="textSecondary">{campaign.percent_cpa}%</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {editingBudget === campaign.id ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <TextField
                          size="small"
                          type="number"
                          value={budgetValue}
                          onChange={(e) => setBudgetValue(e.target.value)}
                          sx={{ width: 80 }}
                        />
                        <IconButton size="small" color="primary" onClick={() => handleBudgetSave(campaign.id)}>
                          <Save fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Tooltip title="Click to edit">
                        <Typography 
                          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          onClick={() => startEditBudget(campaign)}
                        >
                          {parseInt(campaign.client_budget) || 0}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={500}>{campaign.generated_volume}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>€{campaign.cost_per_lead}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>€{campaign.cost_per_lead_positive}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>€{campaign.total_cost}</Typography>
                  </TableCell>
                  <TableCell align="right">€{parseFloat(campaign.payout_per_lead || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography color="success.main" fontWeight={600}>€{campaign.total_revenue}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      fontWeight={600}
                      color={parseFloat(campaign.total_profit) >= 0 ? 'success.main' : 'error.main'}
                    >
                      €{campaign.total_profit}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDialog(campaign)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(campaign.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Campaign Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Add Campaign'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Campaign Name *"
                  value={formData.campaign_name}
                  onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ads Name"
                  value={formData.ads_name}
                  onChange={(e) => setFormData({ ...formData, ads_name: e.target.value })}
                  placeholder="Optional custom name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Facebook IDs (to auto-link leads)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook Page ID"
                  value={formData.fb_page_id}
                  onChange={(e) => setFormData({ ...formData, fb_page_id: e.target.value })}
                  placeholder="447863775075346"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook Ad ID"
                  value={formData.fb_ad_id}
                  onChange={(e) => setFormData({ ...formData, fb_ad_id: e.target.value })}
                  placeholder="120235587781590145"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook Form ID"
                  value={formData.fb_form_id}
                  onChange={(e) => setFormData({ ...formData, fb_form_id: e.target.value })}
                  placeholder="1558474791955116"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook Campaign ID"
                  value={formData.fb_campaign_id}
                  onChange={(e) => setFormData({ ...formData, fb_campaign_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook AdSet ID"
                  value={formData.fb_adset_id}
                  onChange={(e) => setFormData({ ...formData, fb_adset_id: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Budget & Payout
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Client Budget (leads)"
                  value={formData.client_budget}
                  onChange={(e) => setFormData({ ...formData, client_budget: e.target.value })}
                  placeholder="Number of leads"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Payout per Lead (€)"
                  value={formData.payout_per_lead}
                  onChange={(e) => setFormData({ ...formData, payout_per_lead: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  CRM Integration
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.pass_to_crm}
                      onChange={(e) => setFormData({ ...formData, pass_to_crm: e.target.checked })}
                    />
                  }
                  label="Pass leads to CRM"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="n8n Webhook URL"
                  value={formData.n8n_webhook_url}
                  onChange={(e) => setFormData({ ...formData, n8n_webhook_url: e.target.value })}
                  placeholder="https://n8n.example.com/webhook/xxx"
                  helperText="URL to forward leads to CRM via n8n"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default FbAdvertiserCampaigns;
